import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase-config";
import { uploadImageWithMetadata } from "../firebaseActions";
import "../style/Settings.css";

const Settings = () => {
  const today = new Date().toLocaleDateString();

  // State variables for FX settings
  const [euroToXAF, setEuroToXAF] = useState("");
  const [isSavingFX, setIsSavingFX] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [notification, setNotification] = useState("");

  // State variables for password management
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [storedPassword, setStoredPassword] = useState("");
  const roundUpToNearest100 = (value) => {
    return Math.ceil(value / 100) * 100;
  };

  useEffect(() => {
    const fetchSettingsData = async () => {
      try {
        // Fetch FX rates
        const fxDoc = await getDoc(doc(db, "FX", "rates"));
        if (fxDoc.exists()) {
          const data = fxDoc.data();
          setEuroToXAF(data.euroToXAF || "655.957");
        }

        // Fetch current password from Firestore
        const passwordDoc = await getDoc(
          doc(db, "AdminSettings", "credentials")
        );
        if (passwordDoc.exists()) {
          setStoredPassword(passwordDoc.data().currentPassword);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchSettingsData();
  }, []);

  // Handle save button click for FX settings
  const handleSaveClick = async () => {
    setIsSavingFX(true);
    try {
      await setDoc(doc(db, "FX", "rates"), {
        euroToXAF,
      });

      setNotification("FX settings updated successfully!");
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error updating FX settings:", error);
      setNotification("Failed to update FX settings. Please try again.");
    } finally {
      setIsSavingFX(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      setNotification("Please fill in both fields.");
      return;
    }

    setIsChangingPassword(true);
    try {
      const passwordDoc = await getDoc(doc(db, "AdminSettings", "credentials"));
      if (passwordDoc.exists()) {
        const storedPassword = passwordDoc.data().currentPassword;

        if (currentPassword === storedPassword) {
          await setDoc(doc(db, "AdminSettings", "credentials"), {
            currentPassword: newPassword,
            email: passwordDoc.data().email,
          });

          setNotification("Password updated successfully!");
        } else {
          setNotification("Current password is incorrect.");
        }
      } else {
        setNotification("Error: Unable to retrieve stored password.");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setNotification("Failed to update password. Please try again.");
    } finally {
      setIsChangingPassword(false);
      setTimeout(() => setNotification(""), 3000);
    }
  };

  // Handle file change for promotional pictures
  const handlePromoPictureClick = (type) => {
    document.getElementById(`fileInput-${type}`).click();
  };

  // Handle file change for promotional pictures with separate paths for mobile and desktop
  const handleFileChange = async (event, type) => {
    const selectedFile = event.target.files[0];

    if (selectedFile) {
      try {
        const filePath =
          type === "Mobile"
            ? `dkp-promo-mobile/${selectedFile.name}`
            : `dkp-promo-desktop/${selectedFile.name}`;

        await uploadImageWithMetadata(selectedFile, filePath, {
          cacheControl: "public, max-age=31536000",
        });

        const docPath = type === "Mobile" ? "mobile" : "desktop";

        await setDoc(doc(db, "PromoImages", docPath), {
          fileName: selectedFile.name,
          type: type,
        });

        alert(`Promotional picture (${type}) uploaded successfully!`);
      } catch (error) {
        console.error("Error uploading image:", error);
        alert(
          `Failed to upload promotional picture (${type}). Please try again.`
        );
      }
    }
  };

  return (
    <div className="settings-container">
      <div className="header">
        <div className="date">{today}</div>
      </div>
      <div className="title-section">
        <div className="quick-settings-title">Quick Settings</div>
      </div>
      <div className="fx-password-container">
        <div className="fx-settings-section">
          <div className="fx-title">Currency Exchange (FX) Settings</div>
          <div className="fx-group">
            <label>Euro to XAF:</label>
            <input
              type="number"
              value={euroToXAF}
              onChange={(e) => setEuroToXAF(e.target.value)}
              className="fx-input"
              placeholder="655.957"
            />
          </div>
          <button
            className="save-fx-button"
            onClick={handleSaveClick}
            disabled={isSavingFX}
          >
            {isSavingFX ? <span className="loader"></span> : "Save"}
          </button>
        </div>
        <div className="change-password-section">
          <div className="change-password-title">Change Password</div>
          <div className="change-password-form">
            <div className="form-group-unique-settings">
              <label>Enter current password:</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="form-group-unique-settings">
              <label>New Password:</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <button
              className="change-password-button"
              onClick={handleChangePassword}
              disabled={isChangingPassword}
            >
              {isChangingPassword ? (
                <span className="loader"></span>
              ) : (
                "Change Password"
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="promo-pictures-section">
        <div
          className="promo-picture"
          onClick={() => handlePromoPictureClick("Desktop")}
        >
          Change Promotional <br /> Picture (Desktop)
        </div>
        <input
          type="file"
          id="fileInput-Desktop"
          style={{ display: "none" }}
          onChange={(event) => handleFileChange(event, "Desktop")}
        />
        <div
          className="promo-picture"
          onClick={() => handlePromoPictureClick("Mobile")}
        >
          Change Promotional <br /> Picture (Mobile)
        </div>
        <input
          type="file"
          id="fileInput-Mobile"
          style={{ display: "none" }}
          onChange={(event) => handleFileChange(event, "Mobile")}
        />
      </div>
      {notification && (
        <div className={`notification ${notification ? "show" : ""}`}>
          {notification}
        </div>
      )}
    </div>
  );
};

export default Settings;
